import { Procedure } from '@/api';
import {
    Dispatch,
    ReactElement,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UIFormInput, UIIcon, UIIconButton, UIText, UITextButton } from '@/ui';
import { moonlight } from '@/ui/styles';
import { uuidv4 } from '../util';
import styles from './procedures-list.module.scss';
import { useCommonTranslation } from '../hooks';

type ProceduresListProps = {
    procedures: Procedure[];
};

export function ProceduresList(props: ProceduresListProps): ReactElement {
    const { t } = useCommonTranslation();
    const { procedures } = props;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {procedures.map((procedure, index) => (
                <ProcedureItem
                    key={index}
                    procedure={procedure}
                    index={index}
                />
            ))}
            {procedures.length === 0 && (
                <UIText type="default">
                    {t('components.procedures_list.empty_readonly')}
                </UIText>
            )}
        </div>
    );
}

function ProcedureItem(props: {
    procedure: Procedure;
    index: number;
}): ReactElement {
    const { procedure, index } = props;

    return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <UIText>{index + 1}.</UIText>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <UIText>{procedure.text}</UIText>
            </div>
        </div>
    );
}

type EditableProceduresListProps = {
    procedures: Array<ProcedureData>;
    setProcedures: Dispatch<SetStateAction<ProcedureData[]>>;
    showRealTimeErrors: boolean;
    canEdit: boolean;
};
export function EditableProceduresList(
    props: EditableProceduresListProps
): ReactElement {
    const { t } = useCommonTranslation();
    const { procedures, showRealTimeErrors, canEdit, setProcedures } = props;

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const onProcedureChange = useCallback(
        (index: number, data: ProcedureData) => {
            setProcedures((prev) => {
                const newContacts = [...prev];
                newContacts[index] = data;
                return newContacts;
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const shouldRenderAddNewProcedureButton = useMemo(() => {
        if (!canEdit) {
            return false;
        }
        return procedures.length < 50;
    }, [canEdit, procedures]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16
            }}
        >
            {procedures.length > 1 && canEdit ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={procedures}
                        strategy={verticalListSortingStrategy}
                    >
                        {procedures.map((procedure, index) => (
                            <SortableProcedureInput
                                key={procedure.id}
                                id={procedure.id}
                                initialText={procedure.text}
                                index={index}
                                canEdit={canEdit}
                                showRealTimeErrors={showRealTimeErrors}
                                onChange={onProcedureChange}
                                onRemove={() => {
                                    setProcedures((prev) =>
                                        prev.filter((_, i) => i !== index)
                                    );
                                }}
                            />
                        ))}
                    </SortableContext>
                </DndContext>
            ) : (
                procedures.map((procedure, index) => (
                    <ProcedureInput
                        key={procedure.id}
                        id={procedure.id}
                        initialText={procedure.text}
                        index={index}
                        canEdit={canEdit}
                        showRealTimeErrors={showRealTimeErrors}
                        onChange={onProcedureChange}
                        onRemove={() => {
                            setProcedures((prev) =>
                                prev.filter((_, i) => i !== index)
                            );
                        }}
                    />
                ))
            )}

            {shouldRenderAddNewProcedureButton && (
                <div>
                    <UITextButton
                        type="button"
                        variant="primary"
                        onClick={() => {
                            setProcedures((prev) => [
                                ...prev,
                                {
                                    id: uuidv4(),
                                    text: '',
                                    isValid: false
                                }
                            ]);
                        }}
                    >
                        {procedures.length === 0
                            ? t('components.procedures_list.add_procedure')
                            : t('components.procedures_list.add_another')}
                    </UITextButton>
                </div>
            )}
            {!canEdit && procedures.length === 0 && (
                <div>
                    <UIText type="default2">
                        {t('components.procedures_list.empty')}
                    </UIText>
                </div>
            )}
        </div>
    );

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setProcedures((items) => {
                const oldIndex = items.findIndex(
                    (item) => item.id === active.id
                );
                const newIndex = items.findIndex(
                    (item) => item.id === over?.id
                );

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    }
}

export type ProcedureData = Procedure & {
    id: string;
    isValid: boolean;
};

type ProcedureInputProps = {
    id: string;
    initialText: string;
    isDragging?: boolean;
    index: number;
    canEdit: boolean;
    showRealTimeErrors: boolean;
    onChange: (index: number, data: ProcedureData) => void;
    onRemove: () => void;
};
function ProcedureInput(props: ProcedureInputProps): ReactElement {
    const { t } = useCommonTranslation();
    const {
        id,
        initialText,
        isDragging = false,
        index,
        canEdit,
        showRealTimeErrors,
        onChange,
        onRemove
    } = props;

    /**
     * Text
     */
    const [text, setText] = useState(initialText);
    const checkText = useCallback(
        (text: string): string => {
            const trimmed = text.trim();
            if (trimmed.length === 0) {
                return t('components.procedures_list.error_text_required');
            } else if (trimmed.length > 1000) {
                return t('components.procedures_list.error_text_too_long');
            }
            return '';
        },
        [t]
    );
    const textError = useMemo(() => {
        if (!showRealTimeErrors) {
            return '';
        }
        return checkText(text);
    }, [checkText, text, showRealTimeErrors]);

    useEffect(() => {
        const procedure: ProcedureData = {
            id,
            text,
            isValid: !checkText(text)
        };

        onChange(index, procedure);
    }, [id, index, text, textError, checkText, onChange]);

    return (
        <div className={styles.container}>
            <UIText medium>{isDragging ? '#' : index + 1}.</UIText>
            <UIFormInput
                name="text"
                value={text}
                placeholder={t('components.procedures_list.placeholder')}
                type="text"
                autoComplete="on"
                errorText={textError}
                required={canEdit}
                maxLength={1000}
                onChange={setText}
                disabled={!canEdit}
            />
            {canEdit && !isDragging && (
                <UIIconButton
                    icon="trash-2"
                    iconColor="white"
                    variant="danger"
                    type="button"
                    onClick={onRemove}
                />
            )}
        </div>
    );
}

function SortableProcedureInput(
    props: ProcedureInputProps & { id: string }
): ReactElement {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: props.id });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        zIndex: isDragging ? 1000 : 'auto',
        padding: isDragging ? 8 : 0,
        boxShadow: isDragging ? '0 0 10px rgba(0, 0, 0, 0.2)' : 'none',
        borderRadius: 8,
        backgroundColor: 'white'
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <ProcedureInput {...props} isDragging={isDragging} />
            <div
                {...listeners}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 40,
                    width: 40,
                    backgroundColor: moonlight,
                    borderRadius: 8,
                    cursor: 'grab'
                }}
            >
                <UIIcon name="align-justify" size={16} />
            </div>
        </div>
    );
}
